import {isUndefined} from 'lodash';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {makeMapStateToProps} from '../../../../../helpers/reselectHelpers';
import {CommentUnfurlModel, CommentUnfurlTypesEnum} from '../../../../../models/api/commentUnfurlModel';
import {
  InterfaceEventsEnum,
  PopoutLocationsEnum,
  trackInterfaceEvent,
} from '../../../../../services/tracking';
import {AppState} from '../../../../../store/appState';
import {getConversations} from '../../../../../store/data/dataSelectors';
import {getUser} from '../../../../../store/data/teammates/teammatesSelectors';
import {makeDefaultCellsContext} from '../../../../../store/panels/cells/context/cellsContext';
import {NewPanelId} from '../../../../../store/panels/panelStateBase';
import {popoutConversationViewerAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {CommentViewerConversationUnfurl} from './commentViewerConversationUnfurl';

/*
 * Props.
 */

interface CommentViewerConversationUnfurlContainerProps {
  panelId: NewPanelId;
  conversationId: number;
  unfurl: CommentUnfurlModel;
  isInteractionDisabled?: boolean;
}

/*
 * Selectors.
 */

function getUnfurledActivityId(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  if (props.unfurl.type !== CommentUnfurlTypesEnum.MESSAGE) {
    return undefined;
  }

  return props.unfurl.activityId;
}

function getUnfurledConversationId(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  return props.unfurl.conversationId;
}

function getUnfurlError(_state: AppState, props: CommentViewerConversationUnfurlContainerProps) {
  return props.unfurl.error;
}

function makeGetUnfurledConversation() {
  return createSelector([getConversations, getUnfurledConversationId], (conversations, conversationId) =>
    isUndefined(conversationId) ? undefined : conversations[conversationId],
  );
}

function makeGetCellsContext() {
  return createSelector([getUser], (user) => makeDefaultCellsContext(user, false));
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CommentViewerConversationUnfurlContainerProps>()(() => ({
  unfurledConversation: makeGetUnfurledConversation(),
  unfurledActivityId: getUnfurledActivityId,
  cellsContext: makeGetCellsContext(),
  error: getUnfurlError,
}));

export const CommentViewerConversationUnfurlContainer = connect(
  mapStateToProps,
  {popoutConversationViewer: popoutConversationViewerAction},
  (stateProps, dispatchProps, ownProps) => {
    const onUnfurlClick: Handler = () => {
      const {unfurledConversation, unfurledActivityId} = stateProps;
      if (!unfurledConversation) {
        return;
      }

      dispatchProps.popoutConversationViewer(
        unfurledConversation.id,
        unfurledActivityId,
        undefined,
        PopoutLocationsEnum.COMMENT_UNFURL,
      );

      trackInterfaceEvent(InterfaceEventsEnum.P3_CONVERSATION_PREVIEW_CLICK, {
        conversation_id: ownProps.conversationId,
        unfurled_conversation_id: unfurledConversation?.id,
        unfurled_activity_id: unfurledActivityId,
      });
    };
    return {
      ...ownProps,
      ...stateProps,
      onUnfurlClick,
    };
  },
)(CommentViewerConversationUnfurl);
